/**
 * ImprovementTierSection — Autonomy Tier surface (FU5).
 * Shows per-lane tier state (T0/T1/T2), expand eligibility, and a
 * superadmin "Expand to {nextTier}" button with a confirm step.
 * Rendered inside the Loop Health (ImprovementMetrics) tab.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, ChevronRight, Layers, Lock } from 'lucide-react';
import { Badge, Button, Callout, Skeleton } from '../../components/ui/index.js';
import { Can } from '../../components/auth/index.js';
import {
  expandImprovementTierMutation,
  getImprovementMetricsQueryKey,
  getImprovementTierStatusOptions,
  getImprovementTierStatusQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { TierLane } from '../../services/api/improvements.api.js';
import { normalizeApiError } from '../../services/api/errors.js';
import type { ImprovementTierLane } from '../../generated/api/types.gen.js';

// ── Lane display helpers ───────────────────────────────────────────────────────

const LANE_LABELS: Record<TierLane, string> = {
  finance: 'Finance',
  codegen: 'Code Generation',
  template: 'Template Tuning',
  data: 'Data Sources',
  process: 'Process',
};

function laneLabel(lane: TierLane): string {
  return LANE_LABELS[lane] ?? lane;
}

// T0 = neutral/info, T1 = success, T2 = success (per brief)
type TierBadgeVariant = 'neutral' | 'success';

function tierVariant(tier: string): TierBadgeVariant {
  if (tier === 'T1' || tier === 'T2') return 'success';
  return 'neutral';
}

function tierDescription(tier: string): string {
  switch (tier) {
    case 'T0':
      return 'Human-gated — all proposals require explicit approval';
    case 'T1':
      return 'Auto-applies low-risk changes after audit; human gate remains for high-risk';
    case 'T2':
      return 'Lane-budget approvals — full autonomy within configured spend limits';
    default:
      return tier;
  }
}

// ── Expand confirm UI ─────────────────────────────────────────────────────────

interface ExpandConfirmProps {
  lane: ImprovementTierLane;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
  errorMessage: string | null;
}

function ExpandConfirm({ lane, onConfirm, onCancel, isPending, errorMessage }: ExpandConfirmProps) {
  return (
    <div className="mt-3 rounded-lg border border-warning/40 bg-warning/5 p-3 space-y-2">
      <p className="text-xs font-semibold text-heading">
        Expand {laneLabel(lane.lane)} to {lane.nextTier}?
      </p>
      <p className="text-xs text-muted">
        Expansion is a <strong>one-way human decision</strong>. The system will only contract automatically if the
        revert rate exceeds 3% or an integrity violation is detected. You cannot manually downgrade a lane.
      </p>
      {errorMessage && (
        <div className="rounded-sm border border-danger/30 bg-danger-muted px-2 py-1.5 text-xs text-danger">
          {errorMessage}
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="danger" size="sm" onClick={onConfirm} disabled={isPending}>
          {isPending ? 'Expanding…' : `Confirm — expand to ${lane.nextTier}`}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Single lane card ──────────────────────────────────────────────────────────

interface LaneCardProps {
  lane: ImprovementTierLane;
  onExpand: (lane: TierLane, targetTier: 'T1' | 'T2') => void;
  expandingLane: TierLane | null;
  isPending: boolean;
  expandError: string | null;
  onCancelExpand: () => void;
}

function LaneCard({ lane, onExpand, expandingLane, isPending, expandError, onCancelExpand }: LaneCardProps) {
  const isConfirming = expandingLane === lane.lane;

  return (
    <div className="rounded-xl border bg-surface p-4 space-y-3">
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-heading">{laneLabel(lane.lane)}</p>
          <p className="text-xs text-muted mt-0.5">{tierDescription(lane.tier)}</p>
        </div>
        <Badge variant={tierVariant(lane.tier)} className="shrink-0 text-sm font-bold px-3 py-1">
          {lane.tier}
        </Badge>
      </div>

      {/* Next tier path */}
      {lane.nextTier !== null ? (
        <>
          {/* Expand eligible */}
          {lane.expandEligible && (
            <div className="flex items-center gap-1.5 text-xs text-success">
              <CheckCircle size={12} />
              <span>All criteria met — eligible to expand to {lane.nextTier}</span>
            </div>
          )}

          {/* Not yet eligible — show blocking criteria */}
          {!lane.expandEligible && lane.failingCriteria && lane.failingCriteria.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-muted">
                <AlertTriangle size={12} className="text-warning shrink-0" />
                <span>
                  {lane.failingCriteria.length} criterion{lane.failingCriteria.length !== 1 ? 'a' : ''} blocking
                  expansion to {lane.nextTier}:
                </span>
              </div>
              <ul className="space-y-1 pl-4">
                {lane.failingCriteria.map((fc, i) => (
                  <li key={`${lane.lane}-fc-${i}`} className="text-xs text-muted list-disc">
                    <span className="font-medium text-body">{fc.criterion}</span>
                    {fc.detail && <span className="ml-1 text-faint">— {fc.detail}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* No failing criteria but not eligible (edge: backend returned empty array) */}
          {!lane.expandEligible && (!lane.failingCriteria || lane.failingCriteria.length === 0) && (
            <div className="flex items-center gap-1.5 text-xs text-muted">
              <Lock size={12} className="shrink-0" />
              <span>Not yet eligible to expand to {lane.nextTier}</span>
            </div>
          )}

          {/* Expand button — superadmin only, when eligible */}
          {lane.expandEligible && !isConfirming && (
            <Can policy="RequireSuperadmin">
              <Button variant="ghost" size="sm" onClick={() => onExpand(lane.lane, lane.nextTier as 'T1' | 'T2')}>
                <ChevronRight size={12} />
                Expand to {lane.nextTier}
              </Button>
            </Can>
          )}

          {/* Confirm step */}
          {isConfirming && (
            <ExpandConfirm
              lane={lane}
              onConfirm={() => {
                if (lane.nextTier) onExpand(lane.lane, lane.nextTier as 'T1' | 'T2');
              }}
              onCancel={onCancelExpand}
              isPending={isPending}
              errorMessage={expandError}
            />
          )}
        </>
      ) : (
        /* T2 ceiling */
        <div className="flex items-center gap-1.5 text-xs text-success">
          <CheckCircle size={12} />
          <span className="font-medium">Max autonomy — this lane operates at full autonomy (T2)</span>
        </div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function AutonomyTierSection() {
  const queryClient = useQueryClient();

  // Which lane is currently showing the confirm step
  const [confirmingLane, setConfirmingLane] = useState<TierLane | null>(null);
  const [expandError, setExpandError] = useState<string | null>(null);

  const {
    data: lanes,
    isLoading,
    error,
  } = useQuery({ ...getImprovementTierStatusOptions(), staleTime: 30_000, retry: false });

  const expandMutation = useMutation({
    ...expandImprovementTierMutation(),
    onSuccess: () => {
      setConfirmingLane(null);
      setExpandError(null);
      queryClient.invalidateQueries({ queryKey: getImprovementTierStatusQueryKey() });
      queryClient.invalidateQueries({ queryKey: getImprovementMetricsQueryKey() });
    },
    onError: (err: Error) => {
      // Surface 409 criteria-not-met and other API errors inline. The generated client may throw the parsed
      // response body rather than an Error, so it is normalised through `unknown`.
      const thrown: unknown = err;
      setExpandError(thrown instanceof Error ? thrown.message : normalizeApiError(thrown, 0).message);
    },
  });

  function handleExpandClick(lane: TierLane, targetTier: 'T1' | 'T2') {
    if (confirmingLane === lane) {
      // Already in confirm — execute
      setExpandError(null);
      expandMutation.mutate({ body: { lane, targetTier } });
    } else {
      // Open confirm step
      setExpandError(null);
      setConfirmingLane(lane);
    }
  }

  function handleCancelExpand() {
    setConfirmingLane(null);
    setExpandError(null);
  }

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading tier state">
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-surface-alt rounded-t-xl border">
          <Layers size={16} className="text-faint" />
          <h3 className="text-sm font-semibold text-heading">Autonomy Tier</h3>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={`tier-sk-${i}`} className="h-28 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────

  if (error || !lanes) {
    return (
      <Callout tone="danger" icon={AlertTriangle} title="Could not load tier state">
        {(error as Error)?.message ?? 'Unknown error loading autonomy tier.'}
      </Callout>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="rounded-xl border bg-surface overflow-hidden">
      {/* Section header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-surface-alt">
        <Layers size={16} className="text-faint" />
        <h3 className="text-sm font-semibold text-heading">Autonomy Tier</h3>
      </div>

      <div className="p-4 space-y-4">
        {/* Explanation callout */}
        <div className="rounded-lg border bg-surface-alt px-3 py-2.5 text-xs text-muted space-y-1">
          <p>
            <span className="font-medium text-body">T0</span> — human gates every proposal.{' '}
            <span className="font-medium text-body">T1</span> — auto-applies low-risk after audit; human gate for
            high-risk remains. <span className="font-medium text-body">T2</span> — lane-budget approvals, full autonomy
            within limits.
          </p>
          <p className="text-faint">
            Expansion is a one-way superadmin decision. The system auto-contracts on revert rate &ge; 3% or any
            integrity violation.
          </p>
        </div>

        {/* Lane cards grid */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {lanes.map((lane) => (
            <LaneCard
              key={lane.lane}
              lane={lane}
              onExpand={handleExpandClick}
              expandingLane={confirmingLane}
              isPending={expandMutation.isPending}
              expandError={confirmingLane === lane.lane ? expandError : null}
              onCancelExpand={handleCancelExpand}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
